import { useEffect } from 'react';
import {
  Check,
  ClipboardList,
  Contact,
  ShieldCheck,
  ShoppingBag,
  Stethoscope,
  UserRound,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

import { paths } from '~/constants/paths';
import { isIntakeFirstEnabled } from '~/lib/d2c-intake';
import { cn } from '~/lib/utils';
import { useIntakeProgress } from '~/stores/intake-progress-store';
import {
  type IntakeVariant,
  isVariantSwitchEnabled,
  useIntakeVariant,
} from '~/stores/intake-variant-store';

type IntakeStep = {
  path: string;
  title: string;
  icon: React.ReactNode;
};

const INTAKE_STEPS: IntakeStep[] = [
  {
    path: paths.checkoutPatientInfo,
    title: 'Patient Information',
    icon: <UserRound className="size-4" />,
  },
  {
    path: paths.marketplaceDiscover,
    title: 'Select items',
    icon: <ShoppingBag className="size-4" />,
  },
  {
    path: paths.checkoutClinical,
    title: 'Clinical details',
    icon: <Stethoscope className="size-4" />,
  },
  {
    path: paths.checkoutReview,
    title: 'Review',
    icon: <ClipboardList className="size-4" />,
  },
];

/**
 * The D2C mirror breaks intake into smaller steps: demographics first so
 * discovery can start early, and insurance reviewed on its own once discovery
 * has had time to finish.
 */
const D2C_STEPS: IntakeStep[] = [
  { path: paths.checkoutPatientInfo, title: 'Patient', icon: <UserRound className="size-4" /> },
  {
    path: paths.checkoutCoverage,
    title: 'Contact & coverage',
    icon: <Contact className="size-4" />,
  },
  {
    path: paths.marketplaceDiscover,
    title: 'Select items',
    icon: <ShoppingBag className="size-4" />,
  },
  {
    path: paths.checkoutClinical,
    title: 'Clinical details',
    icon: <Stethoscope className="size-4" />,
  },
  { path: paths.checkoutInsurance, title: 'Insurance', icon: <ShieldCheck className="size-4" /> },
  { path: paths.checkoutReview, title: 'Review', icon: <ClipboardList className="size-4" /> },
];

export function getIntakeSteps(variant: IntakeVariant): IntakeStep[] {
  return variant === 'd2c' ? D2C_STEPS : INTAKE_STEPS;
}

/** Active step index, or null when the route sits outside the intake flow. */
export function getActiveStepIdx(
  pathname: string,
  variant: IntakeVariant = 'current'
): number | null {
  const isD2c = variant === 'd2c';
  // Review is last: it is where the order is placed.
  if (pathname.startsWith(paths.checkoutReview) || pathname.startsWith(paths.checkoutComplete)) {
    return isD2c ? 5 : 3;
  }
  if (isD2c && pathname.startsWith(paths.checkoutInsurance)) return 4;
  if (pathname.startsWith(paths.checkoutClinical)) return isD2c ? 3 : 2;
  if (pathname.startsWith(paths.marketplace)) return isD2c ? 2 : 1;
  if (isD2c && pathname.startsWith(paths.checkoutCoverage)) return 1;
  if (pathname.startsWith(paths.checkout)) return 0;
  return null;
}

/**
 * Horizontal progress rail pinned directly beneath the primary nav. Rendered
 * from the root layout so it spans the whole intake flow, including the
 * catalog step, which lives outside the checkout route tree.
 */
export function CheckoutProgressBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { variant, setVariant } = useIntakeVariant();
  const { furthest, markReached } = useIntakeProgress();
  const routeStepIdx = getActiveStepIdx(location.pathname, variant);

  useEffect(() => {
    if (routeStepIdx !== null) markReached(variant, routeStepIdx);
  }, [markReached, routeStepIdx, variant]);

  if (!isIntakeFirstEnabled()) return null;

  const steps = getIntakeSteps(variant);
  const activeIdx = getActiveStepIdx(location.pathname, variant);
  if (activeIdx === null) return null;

  const activeStep = steps[activeIdx];
  // Match the nav's container above so step one lines up with the wordmark.
  const isCheckoutRoute = location.pathname.startsWith(paths.checkout);

  return (
    <div className="shrink-0 border-b border-neutral-200 bg-white">
      <div
        className={cn(
          'mx-auto flex h-14 items-center px-6 tablet:px-8 desktop:px-12',
          isCheckoutRoute ? 'max-w-[2500px]' : 'max-w-[1440px]'
        )}
      >
        {/* Below laptop the four labels cannot sit on one line, so collapse to
            a counter plus a determinate track. */}
        <div className="flex w-full flex-col justify-center gap-1.5 laptop:hidden">
          <div className="flex items-baseline justify-between gap-4">
            <span className="truncate text-sm font-semibold text-black">{activeStep?.title}</span>
            <span className="shrink-0 text-xs text-neutral-500">
              Step {activeIdx + 1} of {steps.length}
            </span>
          </div>
          {/* Decorative: the "Step N of 4" text above already announces progress. */}
          <div aria-hidden className="h-1 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-spruce-500 transition-[width] duration-300"
              style={{ width: `${((activeIdx + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        <nav aria-label="Checkout progress" className="hidden min-w-0 flex-1 laptop:block">
          {/* Capped so connectors stay short enough to read as one group
              instead of stretching across the full content width. */}
          <ol className="mx-auto flex w-full max-w-4xl items-center">
            {steps.map((step, idx) => {
              const furthestReached = Math.max(furthest[variant] ?? activeIdx, activeIdx);
              const isComplete = idx !== activeIdx && idx <= furthestReached;
              const isCurrent = idx === activeIdx;
              const isLast = idx === steps.length - 1;

              return (
                <li key={step.path} className={cn('flex items-center', !isLast && 'flex-1')}>
                  <button
                    type="button"
                    onClick={() => navigate(step.path)}
                    disabled={!isComplete}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={cn(
                      'flex items-center gap-2 rounded-full text-sm whitespace-nowrap',
                      isComplete
                        ? 'cursor-pointer hover:opacity-70'
                        : 'cursor-default disabled:opacity-100'
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-full',
                        isCurrent && 'bg-spruce-100 text-charcoal-900',
                        isComplete && 'bg-spruce-300 text-charcoal-900',
                        !isCurrent && !isComplete && 'bg-neutral-100 text-neutral-500'
                      )}
                    >
                      {isComplete ? <Check className="size-4" /> : step.icon}
                    </span>
                    <span
                      className={cn(
                        isCurrent && 'font-semibold text-black',
                        isComplete && 'text-black',
                        !isCurrent && !isComplete && 'text-neutral-500'
                      )}
                    >
                      {step.title}
                    </span>
                  </button>

                  {!isLast && (
                    <span
                      aria-hidden
                      className={cn(
                        'mx-3 h-px min-w-6 flex-1',
                        isComplete ? 'bg-spruce-300' : 'bg-neutral-200'
                      )}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {isVariantSwitchEnabled() && <VariantSwitch variant={variant} onChange={setVariant} />}
      </div>
    </div>
  );
}

const VARIANT_OPTIONS: { value: IntakeVariant; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'clinical', label: 'Clinical feedback' },
  { value: 'd2c', label: 'D2C mirror' },
];

/** Prototype-only control for comparing the two versions of the flow. */
function VariantSwitch({
  variant,
  onChange,
}: {
  variant: IntakeVariant;
  onChange: (variant: IntakeVariant) => void;
}) {
  return (
    <fieldset
      aria-label="Prototype version"
      className="ml-6 hidden shrink-0 items-center gap-0.5 rounded-lg border border-neutral-200 bg-gray-50 p-0.5 text-xs laptop:flex"
    >
      {VARIANT_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={variant === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-2.5 py-1 font-medium',
            variant === option.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-900'
          )}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}
